import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarcodeInput } from '../components/BarcodeInput';
import { CameraScanner } from '../components/CameraScanner';
import { CartPanel } from '../components/CartPanel';
import { Icon } from '../components/icons';
import { JustAdded } from '../components/JustAdded';
import { CustomerPickerDrawer } from '../components/CustomerPickerDrawer';
import { useSaleAttempt } from '../store/sale-attempt';
import { PaymentScreen } from '../components/PaymentScreen';
import { ProductGrid } from '../components/ProductGrid';
import { QuickFind, type QuickAction } from '../components/QuickFind';
import { ReceiptDialog } from '../components/ReceiptDialog';
import { WeighPad } from '../components/WeighPad';
import { BillDiscountDialog, CloseShiftDialog, HoldDialog, ItemTaxDialog, NoTaxDialog, OpenItemDialog, OpenShiftDialog } from '../components/PosDialogs';
import { FunctionBar } from '../components/FunctionBar';
import { errorMessage, hasKey, LANGUAGES, tr, useLanguage, useT } from '../i18n';
import { Badge, Button, cx, Kbd, Modal, useFeedback, useOnline } from '../components/ui';
import { api, ApiError, isNetworkError } from '../lib/api';
import { amount, humanizeWarning, quantity, rupeesCompact, timeOnly, unitLabel } from '../lib/format';
import { firstName, openCustomerDisplay, useDisplayPublisher, type DisplayState } from '../lib/display';
import type { PaymentPayload } from '../lib/payments';
import type { ScanCoordinator } from '../lib/scan-coordinator';
import { applyStoredThemeFull, parseAccent, parseAppearance, parseContrast } from '../lib/theme';
import { keyForAction, listShortcuts, loadShortcuts, useBrowserKeyGuard, useComboShortcuts, useShortcuts } from '../lib/shortcuts';
import { paletteActions, placeButtons, type TillActionEnv, type TillActionHandlers } from '../till/actions';
import { initialTillView, lineMrpSavingsPaise, mrpSavingsPaise, parseQuickQuery } from '../lib/till';
import { readTerminalId } from '../lib/terminal';
import { useAuth } from '../store/auth';
import { cartDiscountFields, lineDiscountOf, priceCart, useCart } from '../store/cart';
import type { CompleteSaleResult, Customer, HeldSale, HoldPayload, PosProduct, Shift } from '../lib/types';
import { OPEN_ITEM_SKU, normalizeKeyCombo, tillAction, type RoundOffMode, type TaxRemovalMode, type TillLayout } from '@sp/core';

/**
 * The till.
 *
 * Order of operations is deliberate and matches how a counter actually works:
 *   scan -> line in the cart -> F10 -> payment -> invoice saved -> receipt shown -> clear.
 *
 * Totals are computed locally with the shared pricing engine and only then confirmed by the
 * server. If the two ever disagree the cashier is warned and the receipt is withheld: a
 * customer quoted one number must never be handed an invoice for another.
 *
 * Layout: a header that names the register and shows this shift's takings; on the left the
 * scan bar, a confirmation of the last scan and the quick keys; on the right the bill, edge to
 * edge, with the big total and the Pay button. Below 768px the two become tabs with a pay bar
 * pinned to the bottom.
 *
 * Around that core: a search palette that understands "2 kg tomato" and a mobile number (F2),
 * a weigh pad for loose goods, a UPI QR with the amount in it, and a customer-facing display
 * that mirrors all of it on a second screen.
 */

interface TillConfig {
  roundOffMode: RoundOffMode;
  storeStateCode: string | null;
  allowNegativeStock: boolean;
  mergeDuplicateScan: boolean;
  fefoEnabled: boolean;
  maxLineDiscountBps: number;
  maxBillDiscountBps: number;
  invoicePrefix: string;
  upiVpa: string | null;
  /** What this API implements (see `SERVER_FEATURES`); absent on an older API. */
  features?: string[];
  customerProfilesEnabled?: boolean;
  /** The store's default till language; absent on an older API (English). */
  defaultLanguage?: 'en' | 'ta';
  /** Absent on an API older than the function buttons; every one of them means "off". */
  allowBillTaxRemoval?: boolean;
  taxRemovalMode?: TaxRemovalMode;
  customerEnabled?: boolean;
  tillButtons?: TillLayout;
  /** Phone-camera scanning is available only when the store enables it; absent means off. */
  cameraScanEnabled?: boolean;
  /** Store display defaults (ui.default_* settings); absent means system/blue/standard. */
  uiDefaultMode?: string;
  uiDefaultAccent?: string;
  uiDefaultContrast?: string;
  /** Which pane the phone-sized till opens on (ui.mobile_primary_view); absent means items. */
  mobilePrimaryView?: 'items' | 'bill';
  /** Open items; absent on an API older than them, which means "off". */
  openItemEnabled?: boolean;
  openItemMaxPaise?: number;
  openItemPriceIncludesTax?: boolean;
  itemTaxRates?: { id: string; name: string; gstBps: number; cessBps: number }[];
}

interface ShiftSummary {
  billCount: number;
  salesPaise: number;
}

type Tone = 'info' | 'warn' | 'error' | 'good';

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (isNetworkError(error)) return tr('common.error.network');
  return tr('common.error.generic');
}

/** Inter-state supply is IGST rather than CGST + SGST. */
function isInterState(storeStateCode: string | null, customerStateCode: string | null): boolean {
  if (!storeStateCode || !customerStateCode) return false;
  return storeStateCode.toUpperCase() !== customerStateCode.toUpperCase();
}

type DialogName = 'customer' | 'discount' | 'hold' | 'openShift' | 'closeShift' | 'help' | 'noTax' | 'openItem' | 'itemTax' | null;

/** What the receipt screen needs that the server's answer does not carry. */
interface ReceiptMeta {
  mode: 'sale' | 'reprint';
  customer: { name: string; phone: string | null } | null;
  savedPaise: number;
}

export function PosScreen() {
  const principal = useAuth((state) => state.principal);
  const store = useAuth((state) => state.store);
  const canBill = useAuth((state) => state.can('pos.sale.create'));
  const canRemoveTax = useAuth((state) => state.can('pos.sale.taxExempt'));
  const canOpenItem = useAuth((state) => state.can('pos.sale.openItem'));
  const canOverridePrice = useAuth((state) => state.can('pos.sale.priceOverride'));
  const { toast, confirm } = useFeedback();
  const t = useT();
  const language = useLanguage((state) => state.language);
  /** An action's name in this till's language. */
  const actionLabel = (actionId: string, params: Record<string, unknown> | null, fallback: string): string => {
    if (actionId === 'bill.discountPreset') {
      const bps = typeof params?.percentBps === 'number' ? params.percentBps : 0;
      return bps > 0 ? t('action.bill.discountPreset', { percent: `${bps / 100}%` }) : fallback;
    }
    const key = `action.${actionId}` as Parameters<typeof t>[0];
    const text = t(key);
    return text === key ? fallback : text;
  };
  const queryClient = useQueryClient();
  const online = useOnline();

  const lines = useCart((state) => state.lines);
  const billDiscount = useCart((state) => state.billDiscount);
  const customerId = useCart((state) => state.customerId);
  const customerName = useCart((state) => state.customerName);
  const customerPhone = useCart((state) => state.customerPhone);
  const customerStateCode = useCart((state) => state.customerStateCode);
  const lastAddedKey = useCart((state) => state.lastAdded?.key ?? null);
  const taxExempt = useCart((state) => state.taxExempt);
  const taxExemptNote = useCart((state) => state.taxExemptNote);
  const note = useCart((state) => state.note);
  const cartActions = useCart.getState;

  /**
   * The register this till is bound to. The login screen names it, and a session restored from
   * storage — or a principal created without one — still has to find its drawer. Sending no
   * terminal at all makes the API answer with a validation error the operator cannot act on.
   */
  const terminalId = principal?.terminalId ?? readTerminalId();
  const sessionKey = [principal?.userId, principal?.storeId, terminalId].join(':');
  const generation = useCart(s => s.generation);
  const pendingSale = useSaleAttempt(s => s.pending);
  const [sourceHoldId, setSourceHoldId] = useState<string | null>(null);
  const contextRef = useRef({ sessionKey, generation, blocked: false });


  // Shortcut badges (Kbd) read the module table at render: refresh once the server
  // table lands, or an admin remap keeps showing FALLBACK until an unrelated re-render.
  const [, setShortcutVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    void loadShortcuts().then(() => {
      if (alive) setShortcutVersion((version) => version + 1);
    });
    return () => {
      alive = false;
    };
  }, []);

  const [config, setConfig] = useState<TillConfig | null>(null);
  const [shift, setShift] = useState<Shift | null>(null);
  const [shiftChecked, setShiftChecked] = useState(false);
  const [configFailed, setConfigFailed] = useState(false);
  const [shiftFailed, setShiftFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<CompleteSaleResult | null>(null);
  const [receiptMeta, setReceiptMeta] = useState<ReceiptMeta | null>(null);
  const [lastReceipt, setLastReceipt] = useState<{ result: CompleteSaleResult; meta: ReceiptMeta } | null>(null);
  const [dialog, setDialog] = useState<DialogName>(null);
  const [customerPhoneHint, setCustomerPhoneHint] = useState<string | undefined>(undefined);
  const [finding, setFinding] = useState<{ query: string } | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  /**
   * Bill generation for the scan pipeline: advanced on every bill boundary so a late
   * lookup can never leak into the next customer's bill. The coordinator (owned by
   * BarcodeInput) enforces it; the till only advances it.
   */
  const scannerHandle = useRef<ScanCoordinator<PosProduct> | null>(null);
  const [weighing, setWeighing] = useState<{ product: PosProduct; initial?: { mode: 'weight' | 'amount'; text: string } } | null>(null);
  const [upiOnDisplay, setUpiOnDisplay] = useState<{ uri: string; amountPaise: number } | null>(null);
  /** The last sale, kept on the customer display for a few seconds after the till moves on. */
  const [thankYou, setThankYou] = useState<Extract<DisplayState, { kind: 'done' }> | null>(null);
  const [offline, setOffline] = useState(false);
  const [openingShift, setOpeningShift] = useState(false);
  const [view, setView] = useState<'items' | 'bill'>('items');
  /** The store's default pane applies once per load; a switch made during the session is never stomped. */
  const viewDefaultApplied = useRef(false);
  /** One idempotency key per bill attempt; reused only while the attempt is byte-identical. */
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);

  contextRef.current = { sessionKey, generation, blocked: paymentOpen || dialog !== null || submitting || Boolean(pendingSale) };
  useEffect(() => {
    if (!pendingSale) return;
    function warn(e: BeforeUnloadEvent) { e.preventDefault(); e.returnValue = ''; }
    function guard(e: MouseEvent) { if ((e.target as HTMLElement)?.closest('a[href]')) { e.preventDefault(); e.stopPropagation(); } }
    window.addEventListener('beforeunload', warn); document.addEventListener('click', guard, true);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', guard, true); };
  }, [pendingSale]);

  const flash = useCallback((tone: Tone, message: string) => toast(tone, message), [toast]);

  useEffect(() => {
    void loadShortcuts();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setConfigFailed(false);
    setShiftFailed(false);
    setShiftChecked(false);

    api
      .get<TillConfig>('/api/pos/config')
      .then((value) => {
        if (!cancelled) {
          setConfig(value);
          useLanguage.getState().applyStoreDefault(value.defaultLanguage);
          // The phone opens on the store's chosen pane (Items or the Bill check view), once
          // per load; a switch the cashier makes during the session is never stomped.
          if (!viewDefaultApplied.current) {
            viewDefaultApplied.current = true;
            setView(initialTillView(value));
          }
          // Store display defaults take effect only where the terminal saved nothing:
          // stored choice first, store default second, built-ins last. Attribute-only,
          // so cart, payment draft and pending attempts are untouched.
          try {
            applyStoredThemeFull(localStorage, document.documentElement, {
              appearance: parseAppearance(value.uiDefaultMode),
              accent: parseAccent(value.uiDefaultAccent),
              contrast: parseContrast(value.uiDefaultContrast),
            });
          } catch {
            // Blocked storage or DOM: the boot-time theme choice stands.
          }
        }
      })
      .catch(() => {
        // Without the round-off mode the till cannot show a trustworthy total, so say so
        // rather than quietly guessing and disagreeing with the invoice later.
        if (!cancelled) setConfigFailed(true);
      });

    // The terminal is always named: without it the API refuses the call outright, and the till
    // would sit on "checking" forever while also offering to open a shift.
    api
      .get<Shift | null>('/api/pos/shifts/current', { terminalId })
      .then((value) => {
        if (!cancelled) {
          setShift(value);
          setShiftChecked(true);
        }
      })
      .catch(() => {
        // A failed shift lookup is its own state, not a broken bill: the drawer is unknown, the
        // totals are still trustworthy, and the message says exactly that.
        if (!cancelled) {
          setShiftFailed(true);
          setShiftChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [terminalId, reloadKey]);

  const interState = isInterState(config?.storeStateCode ?? null, customerStateCode);
  const roundOffMode = config?.roundOffMode ?? 'rupee';

  const { priced, totals, taxReliefPaise } = useMemo(
    () => priceCart(lines, billDiscount, interState, roundOffMode, taxExempt),
    [lines, billDiscount, interState, roundOffMode, taxExempt],
  );
  const features = config?.features ?? [];
  const customerEnabled = config?.customerEnabled !== false;
  const taxRemovalAllowed = config?.allowBillTaxRemoval === true && features.includes('taxExempt');
  const taxRemovalMode: TaxRemovalMode = config?.taxRemovalMode ?? 'discount';
  const openItemEnabled = config?.openItemEnabled === true && features.includes('openItem');
  const openItemPriceIncludesTax = config?.openItemPriceIncludesTax !== false;
  /** The rate an open item carries until Item Tax picks another: the store's 0% rate. */
  const openItemNoTax = (config?.itemTaxRates ?? []).find((rate) => rate.gstBps === 0 && rate.cessBps === 0) ?? {
    id: 'tax:gst-0-',
    name: 'GST 0%',
    gstBps: 0,
    cessBps: 0,
  };

  const heldQuery = useQuery({
    queryKey: ['till', 'holds', terminalId],
    enabled: shift !== null,
    queryFn: () =>
      api
        .get<{ holds: HeldSale[] }>('/api/pos/holds', { terminalId })
        .then((result) => result.holds),
  });
  const heldCount = heldQuery.data?.length ?? 0;

  const summaryQuery = useQuery({
    queryKey: ['till', 'shift-summary', shift?.id ?? null],
    enabled: shift !== null,
    refetchInterval: 120_000,
    queryFn: () => api.get<ShiftSummary>(`/api/pos/shifts/${shift!.id}/summary`),
  });

  /** Opens the till with the float the cashier counted into the drawer. */
  const openShift = async (openingCashPaise: number) => {
    setOpeningShift(true);
    try {
      const opened = await api.post<Shift>('/api/pos/shifts/open', {
        terminalId,
        openingCashPaise,
      });
      setShift(opened);
      setDialog(null);
      flash('good', t('pos.shiftOpened'));
    } catch (error) {
      flash('error', messageOf(error));
    } finally {
      setOpeningShift(false);
    }
  };

  /**
   * Batch- and expiry-tracked goods must go through FEFO, so a scan of them is never added
   * straight to the cart: the server picks the batch that expires first.
   */
  const addWithFefo = useCallback(
    async (product: PosProduct, qtyMilli: number) => {
      const origin = { ...contextRef.current };
      try {
        const result = await api.post<{
          candidates: { batchId: string; batchNumber: string | null; expiryDate: string | null }[];
        }>('/api/pos/batches/resolve', { productId: product.id, qtyMilli });
        if (origin.sessionKey !== contextRef.current.sessionKey || origin.generation !== contextRef.current.generation || contextRef.current.blocked) return;
        const first = result.candidates[0];
        if (!first) {
          flash('warn', t('pos.noBatch', { name: product.name }));
          return;
        }
        // The batch number is shown to the operator; NO batch id goes into the cart line. A line that
        // carries one is read by the API as a manual override, and any role without
        // pos.sale.batchOverride is then refused outright — the till's own FEFO pre-pick must not
        // masquerade as a choice the cashier made. The server allocates the same first-expiring batch.
        cartActions().addBatchLine(product, null, first.batchNumber, first.expiryDate);
        const added = cartActions().lastAdded;
        if (added && qtyMilli > added.qtyMilli) {
          cartActions().increment(added.key, qtyMilli - added.qtyMilli);
          useCart.setState({ lastAdded: { key: added.key, qtyMilli } });
        }
        const batch = first.batchNumber ? tr('pos.batchNo', { batch: first.batchNumber }) : tr('pos.batch');
        const expiry = first.expiryDate ? ` ${tr('pos.expires', { date: first.expiryDate })}` : '';
        flash('good', `${product.name} · ${batch}${expiry}`);
      } catch (error) {
        flash('error', messageOf(error));
      }
    },
    [cartActions, flash],
  );

  /**
   * One way onto the bill for every source: a scan, a tile, the search palette, the weigh pad.
   * Loose goods with no quantity yet open the weigh pad; an explicit quantity is added as given.
   */
  const addProduct = useCallback(
    (product: PosProduct, options: { qtyMilli?: number | null; amountPaise?: number | null } = {}) => {
      if (!shift) {
        flash('warn', tr('pos.needShift'));
        return;
      }
      const qtyMilli = options.qtyMilli && options.qtyMilli > 0 ? options.qtyMilli : null;
      if (product.allowDecimalQty && qtyMilli === null) {
        setWeighing({
          product,
          initial: options.amountPaise ? { mode: 'amount', text: amount(options.amountPaise).replace(/\.00$/, '') } : undefined,
        });
        return;
      }
      if (config?.fefoEnabled && (product.isBatchTracked || product.isExpiryTracked)) {
        if (product.stockMilli <= 0) {
          flash('warn', tr('pos.outOfStock', { name: product.name }));
          return;
        }
        void addWithFefo(product, qtyMilli ?? 1000);
        return;
      }
      if (qtyMilli !== null) cartActions().addQuantity(product, qtyMilli);
      else cartActions().addProduct(product);
    },
    [shift, config?.fefoEnabled, addWithFefo, cartActions, flash],
  );

  const onScan = useCallback(
    ({ product, scanned, error }: { product: PosProduct | null; scanned: string; error?: 'timeout' | 'offline' | 'auth-expired' | 'forbidden' | 'server-error' | 'queue-overflow' }) => {
      // Transport failures must never open the customer dialog below: a 10-digit code
      // during an outage is an outage, not a phone number. onScanError already flashed it.
      if (error) return;
      if (!shift) {
        flash('warn', tr('pos.needShift'));
        return;
      }
      if (!product) {
        // Ten digits that are not a barcode are almost always the customer's mobile number.
        // Normalise Tamil digits first: a number typed in Tamil must open the customer
        // dialog, not "No product carries barcode".
        const ascii = scanned.replace(/[௦-௯]/g, (digit) => String('௦௧௨௩௪௫௬௭௮௯'.indexOf(digit)));
        if (customerEnabled && /^[6-9]\d{9}$/.test(ascii)) {
          setCustomerPhoneHint(ascii);
          setDialog('customer');
          return;
        }
        flash('error', tr('pos.noBarcode', { code: scanned }));
        return;
      }
      if (product.stockMilli <= 0) {
        flash('warn', tr('pos.outOfStock', { name: product.name }));
        return;
      }
      addProduct(product);
    },
    [shift, addProduct, flash, customerEnabled],
  );

  const startPayment = useCallback(() => {
    if (!shift) {
      flash('warn', tr('pos.needShift'));
      return;
    }
    if (useCart.getState().lines.length === 0) {
      flash('warn', tr('pos.needItem'));
      return;
    }
    setPaymentError(null);
    setPaymentOpen(true);
  }, [shift, flash]);

  const startNewSale = async () => {
    if (pendingSale) return;
    // Clearing a bill with items on it throws work away: ask first.
    if (cartActions().lines.length > 0) {
      const ok = await confirm({
        title: t('pos.clearTitle'),
        message: t('pos.clearMessage'),
        confirmLabel: t('pos.clearConfirm'),
        danger: true,
      });
      if (!ok) return;
    }
    cartActions().clear();
    setSourceHoldId(null);
    scannerHandle.current?.newGeneration();
    setPaymentOpen(false);
    setReceipt(null);
    attempt.current = null;
    setView('items');
  };

  async function completeSale(payments: PaymentPayload[]) {
    if (submitting || pendingSale) return;
    setSubmitting(true);
    setPaymentError(null);
    const quotedTotal = totals.grandTotalPaise;
    // Captured before the till clears: the receipt screen and the customer display still need them.
    const meta: ReceiptMeta = {
      mode: 'sale',
      customer: customerName ? { name: customerName, phone: customerPhone } : null,
      savedPaise: mrpSavingsPaise(lines) + totals.totalDiscountPaise - taxReliefPaise,
    };

    const body = {
      lines: lines.map((line) => ({
        productId: line.productId,
        qtyMilli: line.qtyMilli,
        ratePaise: line.ratePaise,
        batchId: line.batchId,
        discount: lineDiscountOf(line),
        ...(line.openItem ? { openItem: { name: line.openItem.name, taxRateId: line.openItem.taxRateId } } : {}),
      })),
      billDiscount,
      payments,
      customerId,
      heldSaleId: sourceHoldId,
      note: useCart.getState().note,
      terminalId,
      shiftId: shift?.id ?? null,
      // The server refuses the sale (TOTAL_MISMATCH) rather than invoicing a different amount.
      expectedGrandTotalPaise: quotedTotal,
      // Sent only when on, so an ordinary bill's request is exactly what it always was.
      ...(taxExempt ? { taxExempt: true, taxExemptNote } : {}),
    };

    // One key per bill attempt. If the request times out and the cashier presses the key
    // again, the server returns the ORIGINAL invoice instead of billing the customer twice.
    // A changed bill or payment is a different attempt and gets a new key.
    const fingerprint = JSON.stringify(body);
    if (attempt.current?.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, key: crypto.randomUUID() };
    }

    const submitted = { session: sessionKey, key: attempt.current.key, body, total: quotedTotal };
    useSaleAttempt.getState().set(submitted);
    try {
      const result = await api.post<CompleteSaleResult>('/api/pos/sales', { ...body, idempotencyKey: submitted.key });
      useSaleAttempt.getState().set(null);
      setSourceHoldId(null);

      attempt.current = null;
      setOffline(false);
      setPaymentOpen(false);
      setUpiOnDisplay(null);
      cartActions().clear();
      scannerHandle.current?.newGeneration();
      setView('items');

      if (result.grandTotalPaise !== quotedTotal) {
        flash(
          'error',
          tr('pos.totalMismatch', { invoice: result.invoiceNumber, actual: amount(result.grandTotalPaise), quoted: amount(quotedTotal) }),
        );
      } else {
        for (const warning of result.warnings) flash('warn', humanizeWarning(warning));
        flash('good', tr('pos.invoiceSaved', { invoice: result.invoiceNumber }));
      }
      setReceipt(result);
      setReceiptMeta(meta);
      setLastReceipt({ result, meta: { ...meta, mode: 'reprint' } });
      setThankYou({
        kind: 'done',
        storeName: store?.name ?? tr('till.ourStore'),
        customerFirstName: firstName(meta.customer?.name),
        invoiceNumber: result.invoiceNumber,
        totalPaise: result.grandTotalPaise,
        changePaise: result.changePaise,
        savedPaise: meta.savedPaise,
      });
      void queryClient.invalidateQueries({ queryKey: ['till', 'products'] });
      void queryClient.invalidateQueries({ queryKey: ['till', 'shift-summary'] });
      // A resumed bill is consumed by the sale; without this the held badge and list keep offering it.
      if (sourceHoldId) void queryClient.invalidateQueries({ queryKey: ['till', 'holds'] });
    } catch (error) {
      if (isNetworkError(error)) {
        setOffline(true);
        const message =
          t('outcomeUnknown');
        setPaymentError(message);
        flash('error', message);
      } else {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.code !== 'BAD_RESPONSE') useSaleAttempt.getState().set(null);
        setPaymentError(messageOf(error));
        flash('error', messageOf(error));
      }
    } finally {
      setSubmitting(false);
    }
  }


  async function resolveSubmittedSale() {
    if (!pendingSale || pendingSale.session !== sessionKey || submitting) return;
    setSubmitting(true);
    try {
      const result = await api.post<CompleteSaleResult>('/api/pos/sales', { ...pendingSale.body, idempotencyKey: pendingSale.key });
      useSaleAttempt.getState().set(null); attempt.current = null; setSourceHoldId(null);
      cartActions().clear(); setPaymentOpen(false); setPaymentError(null); setReceipt(result); setReceiptMeta({ mode: 'sale', customer: null, savedPaise: 0 }); setOffline(false);
      void queryClient.invalidateQueries({ queryKey: ['till'] });
    } catch (error) {
      // A refusal on a later retry (including expired auth) cannot prove that the first request failed.
      // Keep the original attempt locked until its successful idempotent result is obtained.
      flash('error', messageOf(error));
    } finally { setSubmitting(false); }
  }

  // The thank-you leaves the customer display when the next customer's first item arrives, or
  // after a few seconds, whichever is first.
  useEffect(() => {
    if (!thankYou) return;
    if (lines.length > 0) {
      setThankYou(null);
      return;
    }
    const timer = window.setTimeout(() => setThankYou(null), 10_000);
    return () => window.clearTimeout(timer);
  }, [thankYou, lines.length]);

  const displayState = useMemo<DisplayState>(() => {
    const base = { storeName: store?.name ?? tr('till.ourStore'), customerFirstName: firstName(customerName) };
    if (lines.length === 0) return thankYou ?? { ...base, kind: 'idle' };
    const bill = {
      ...base,
      lines: lines.map((line, index) => {
        const qty = quantity(line.qtyMilli);
        const rate = amount(line.ratePaise);
        return {
          name: line.productName,
          detail: line.allowDecimalQty ? `${qty} ${unitLabel(line.unit)} × ₹${rate}` : `${qty} × ₹${rate}`,
          amountPaise: priced[index]?.totalPaise ?? 0,
          savedPaise: lineMrpSavingsPaise(line),
        };
      }),
      totalPaise: totals.grandTotalPaise,
      savedPaise: mrpSavingsPaise(lines) + totals.totalDiscountPaise - taxReliefPaise,
      lastIndex: lines.findIndex((line) => line.key === lastAddedKey),
    };
    if (paymentOpen) {
      return {
        ...bill,
        kind: 'pay',
        upiUri: upiOnDisplay?.uri ?? null,
        upiPayee: store?.name ?? null,
        upiAmountPaise: upiOnDisplay?.amountPaise ?? 0,
      };
    }
    return { ...bill, kind: 'bill' };
  }, [language, store?.name, customerName, lines, priced, totals, taxReliefPaise, lastAddedKey, paymentOpen, upiOnDisplay, thankYou]);
  useDisplayPublisher(displayState);

  const focusBySelector = (selector: string): boolean => {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) return false;
    // A hidden input (mobile bill pane before layout) reports no size: focusing it
    // drops focus entirely, so report failure and let the caller retry next frame.
    if (element.offsetParent === null && element !== document.activeElement) {
      // Still focus if it is the scan box (always laid out); otherwise retry.
      if (!selector.includes('cart-line')) element.focus();
      else return false;
    }
    element.focus();
    if (element instanceof HTMLInputElement) element.select();
    return document.activeElement === element;
  };

  const openFind = useCallback((query = '') => {
    setView('items');
    setFinding({ query });
  }, []);

  /**
   * The scan bar takes words too. A plain name that matches exactly one product goes straight
   * onto the bill — the second Enter was pure friction. Anything carrying a quantity or a rupee
   * amount keeps the palette, where the parsed quantity is shown before anything is added.
   */
  const scanTextEpoch = useRef(0);
  const modalOpenRef = useRef(false);
  const onScanText = useCallback(
    (text: string) => {
      const parsed = parseQuickQuery(text);
      if (parsed.kind !== 'item' || parsed.quantity !== null || parsed.amountPaise !== null || !shift) {
        openFind(text);
        return;
      }
      const epoch = ++scanTextEpoch.current;
      void api
        .get<{ items: PosProduct[]; total: number }>('/api/catalog/products', { q: parsed.term, page: 0, pageSize: 2 })
        .then((result) => {
          // A newer scan, or a palette/dialog the cashier opened meanwhile, owns the screen now.
          if (epoch !== scanTextEpoch.current || modalOpenRef.current) return;
          if (result.total === 1 && result.items.length === 1) addProduct(result.items[0]!);
          else openFind(text);
        })
        .catch(() => {
          if (epoch === scanTextEpoch.current && !modalOpenRef.current) openFind(text);
        });
    },
    [shift, addProduct, openFind],
  );

  const reprintLast = () => {
    if (lastReceipt) {
      setReceipt(lastReceipt.result);
      setReceiptMeta(lastReceipt.meta);
    } else flash('info', tr('pos.noReceipt'));
  };

  const removeLast = () => {
    const last = lines[lines.length - 1];
    if (!last) return;
    cartActions().removeLine(last.key);
    flash('info', tr('pos.removed', { name: last.productName }));
  };

  // F5 / F11 must never reach the browser, even while a dialog owns the keyboard.
  useBrowserKeyGuard(true);

  useShortcuts(
    {
      productSearch: () => openFind(),
      quantity: () => {
        setView('bill');
        // On mobile the bill pane is display:none until the re-render commits: a bare
        // setTimeout(0) can focus a hidden input and drop focus entirely. Retry on the
        // next frame so the pane is laid out first.
        const focusLastQty = (attempts: number) => {
          const found = focusBySelector('[data-testid="cart-line"]:last-child input');
          if (!found && attempts > 0) window.requestAnimationFrame(() => focusLastQty(attempts - 1));
        };
        window.requestAnimationFrame(() => focusLastQty(3));
      },
      payment: startPayment,
      newSale: () => void startNewSale(),
      discount: () => setDialog('discount'),
      customer: () => (customerEnabled ? setDialog('customer') : undefined),
      customerLookup: () => (customerEnabled ? setDialog('customer') : undefined),
      holdBill: () => setDialog('hold'),
      resumeBill: () => setDialog('hold'),
      reprintReceipt: reprintLast,
      removeItem: removeLast,
    },
    // Dialogs own the keyboard while they are open.
    dialog === null && !paymentOpen && receipt === null && finding === null && weighing === null,
  );

  // "?" opens the shortcut help from anywhere on the till, unless a field is being typed in.
  // Ctrl+K (⌘K) opens the search palette, even from the scan bar.
  const modalOpen = dialog !== null || paymentOpen || receipt !== null || finding !== null || weighing !== null;
  // The scan-bar word path reads this at resolve time so a late match can never land on a
  // screen the cashier has already moved on from.
  modalOpenRef.current = modalOpen;
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (modalOpen) return;
      // IME composition (Tamil) must reach the field, never the till.
      if (event.isComposing || event.keyCode === 229) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openFind();
        return;
      }
      if (event.key !== '?' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLElement && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return;
      event.preventDefault();
      setDialog('help');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, openFind]);

  const sold = lines.length > 0;
  const keyOf = (action: string, fallback: string) => keyForAction(action) ?? fallback;
  /**
   * The server answered for this register. A failed lookup is NOT the same as "no drawer is
   * open": claiming that, and offering to open one, is how a cashier ends up with two shifts.
   */
  const shiftKnown = shiftChecked && !shiftFailed;

  // ── function buttons ─────────────────────────────────────────────────────────────────────
  const toggleNoTax = () => {
    if (taxExempt) {
      cartActions().setTaxExempt(null);
      flash('info', tr('pos.taxBack'));
      return;
    }
    setDialog('noTax');
  };

  const applyDiscountPreset = (bps: number) => {
    const limit = config?.maxBillDiscountBps ?? 0;
    if (bps > limit) {
      flash('warn', tr('pos.discountBeyond', { percent: bps / 100, limit: limit / 100 }));
      return;
    }
    cartActions().setBillDiscount({ type: 'percent', bps });
    flash('good', tr('pos.discountApplied', { percent: bps / 100 }));
  };

  const removeCustomer = () => {
    if (!customerId) return;
    cartActions().attachCustomer(null);
    flash('info', tr('pos.customerRemoved'));
  };

  const actionEnv: TillActionEnv = {
    lineCount: lines.length,
    hasCustomer: customerId !== null,
    heldCount,
    shiftOpen: shift !== null,
    canBill,
    hasLastReceipt: lastReceipt !== null,
    taxRemovalAllowed,
    canRemoveTax,
    taxExempt: taxExempt !== null,
    customerEnabled,
    features,
    openItemEnabled,
    canOpenItem,
    hasOpenItemLines: lines.some((line) => line.openItem),
  };
  const actionHandlers: TillActionHandlers = {
    find: () => openFind(),
    attachCustomer: () => setDialog('customer'),
    removeCustomer,
    editQuantity: () => {
      setView('bill');
      const focusLastQty = (attempts: number) => {
        const found = focusBySelector('[data-testid="cart-line"]:last-child input');
        if (!found && attempts > 0) window.requestAnimationFrame(() => focusLastQty(attempts - 1));
      };
      window.requestAnimationFrame(() => focusLastQty(3));
    },
    removeLast,
    hold: () => setDialog('hold'),
    resume: () => setDialog('hold'),
    discount: () => setDialog('discount'),
    discountPreset: applyDiscountPreset,
    pay: startPayment,
    reprint: reprintLast,
    newBill: () => void startNewSale(),
    toggleNoTax,
    customerDisplay: openCustomerDisplay,
    openItem: () => setDialog('openItem'),
    itemTax: () => setDialog('itemTax'),
  };
  // An API older than the buttons sends no layout: no bar, exactly as before.
  const placed =
    features.includes('tillButtons') && config?.tillButtons
      ? placeButtons(config.tillButtons.buttons, actionEnv, actionHandlers, language).map((entry) =>
          // A label the store typed wins; otherwise the action's own name in this till's language.
          entry.button.label?.[language] || entry.button.label?.en ? entry : { ...entry, label: actionLabel(entry.button.actionId, entry.button.params, entry.label) },
        )
      : [];
  /** Built-in actions take their key from the shortcut table; the rest from their button. */
  const keyOfButton = (entry: (typeof placed)[number]) => {
    const legacy = tillAction(entry.button.actionId)?.legacy;
    return legacy ? (keyForAction(legacy) ?? null) : normalizeKeyCombo(entry.button.keyCombo);
  };
  // Legacy keys and button keys share one keyboard: a button must not steal a key the
  // shortcut table already owns, or one press fires two actions.
  const legacyCombos = new Set(
    listShortcuts()
      .map((entry) => normalizeKeyCombo(entry.keyCode))
      .filter((combo): combo is string => combo !== null),
  );
  const comboBindings = new Map<string, () => void>();
  for (const entry of placed) {
    if (tillAction(entry.button.actionId)?.legacy) continue;
    const combo = normalizeKeyCombo(entry.button.keyCombo);
    if (!combo || legacyCombos.has(combo) || comboBindings.has(combo)) continue;
    comboBindings.set(combo, () => {
      if (entry.action.disabledReason) flash('warn', `${entry.label}: ${entry.action.disabledReason}`);
      else entry.action.run(entry.button.params);
    });
  }
  useComboShortcuts(comboBindings, dialog === null && !paymentOpen && receipt === null && finding === null && weighing === null);

  /** Everything the till can do, for the search palette. Each closes the palette first. */
  const actions: QuickAction[] = ([
    { id: 'customer', label: t('palette.customer'), hint: t('palette.customerHint'), shortcut: keyOf('customer', 'F3'), icon: 'user', keywords: 'phone mobile loyalty', run: () => setDialog('customer') },
    { id: 'pay', label: t('action.payment.take'), hint: sold ? t('palette.payDue', { amount: amount(totals.grandTotalPaise) }) : t('palette.addItemFirst'), shortcut: keyOf('payment', 'F10'), icon: 'receipt', keywords: 'pay cash upi card checkout', disabled: !sold || !canBill, run: startPayment },
    { id: 'hold', label: t('palette.hold'), hint: t('palette.holdHint'), shortcut: keyOf('holdBill', 'F6'), icon: 'pause', keywords: 'park later', disabled: !sold, run: () => setDialog('hold') },
    { id: 'resume', label: t('palette.resume'), hint: heldCount > 0 ? t('palette.waiting', { count: heldCount }) : t('palette.noneWaiting'), shortcut: keyOf('resumeBill', 'F9'), icon: 'inbox', keywords: 'held parked', disabled: sold || heldCount === 0, run: () => setDialog('hold') },
    { id: 'discount', label: t('action.bill.discount'), hint: t('palette.discountHint'), shortcut: keyOf('discount', 'F7'), icon: 'tag', keywords: 'offer percent off', disabled: !sold, run: () => setDialog('discount') },
    { id: 'reprint', label: lastReceipt ? t('palette.reprintNumbered', { invoice: lastReceipt.result.invoiceNumber }) : t('action.bill.reprint'), hint: t('palette.reprintHint'), shortcut: keyOf('reprintReceipt', 'F11'), icon: 'printer', keywords: 'duplicate copy print', disabled: !lastReceipt, run: reprintLast },
    { id: 'new', label: t('action.bill.new'), hint: t('palette.newHint'), shortcut: keyOf('newSale', 'Alt+N'), icon: 'plus', keywords: 'clear next', run: () => void startNewSale() },
    { id: 'display', label: t('till.displayOpen'), hint: t('palette.displayHint'), icon: 'monitor', keywords: 'display second monitor qr', run: openCustomerDisplay },
    { id: 'close', label: t('till.closeShift'), hint: t('palette.closeHint'), icon: 'lock', keywords: 'end day drawer cash up', disabled: !shift || sold, run: () => setDialog('closeShift') },
    { id: 'help', label: t('till.help'), shortcut: '?', icon: 'menu', keywords: 'help keys', run: () => setDialog('help') },
    // Function-button actions with no entry above, so every action is searchable even without a button.
    ...paletteActions(actionEnv, actionHandlers)
      .filter((action) => action.id !== 'system.customerDisplay')
      .map((action) => ({
        id: action.id,
        label: action.pressed ? t('palette.on', { label: action.label }) : action.label,
        hint: action.disabledReason ?? undefined,
        icon: action.icon,
        keywords: action.id === 'bill.noTax' ? 'tax gst exempt remove' : action.id === 'customer.remove' ? 'detach clear walk-in' : undefined,
        disabled: action.disabledReason !== null,
        run: () => action.run(null),
      })),
  ] satisfies QuickAction[])
    .filter((action) => customerEnabled || action.id !== 'customer')
    .map((action) => ({ ...action, run: () => { setFinding(null); action.run(); } }));

  return (
    <div className="flex h-full flex-col" style={{ background: 'var(--ui-bg)' }}>
      <TillHeader
        shift={shift}
        shiftKnown={shiftKnown}
        shiftFailed={shiftFailed}
        terminal={terminalId}
        cashier={principal?.fullName ?? null}
        storeName={store?.name ?? null}
        online={online && !offline}
        heldCount={heldCount}
        summary={summaryQuery.data ?? null}
        onOpenShift={() => setDialog('openShift')}
        onCloseShift={() => setDialog('closeShift')}
        onHold={() => setDialog('hold')}
        onHelp={() => setDialog('help')}
        onDisplay={openCustomerDisplay}
        resumeKey={keyOf('resumeBill', 'F9')}
        canClose={!sold}
      />

      {configFailed ? (
        <div className="banner banner-danger mx-3 mt-3 items-center justify-between" role="alert" data-testid="till-load-error">
          <span>{t('till.configFailed')}</span>
          <Button size="sm" onClick={() => setReloadKey((value) => value + 1)}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {shiftFailed ? (
        <div className="banner banner-warn mx-3 mt-3 items-center justify-between" role="status" data-testid="till-shift-error">
          <span>{t('till.shiftCheckFailed')}</span>
          <Button size="sm" onClick={() => setReloadKey((value) => value + 1)}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      <div className="flex shrink-0 gap-2 px-3 pt-3 md:hidden" role="group" aria-label={t('till.view')}>
        <button type="button" className="chip flex-1 justify-center" aria-pressed={view === 'items'} onClick={() => setView('items')}>
          {t('till.items')}
        </button>
        <button type="button" className="chip flex-1 justify-center" aria-pressed={view === 'bill'} onClick={() => setView('bill')}>
          {t('till.bill')} ({lines.length}) · ₹{amount(totals.grandTotalPaise)}
        </button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(21rem,25rem)] lg:grid-cols-[minmax(0,1fr)_minmax(25rem,28rem)]">
        <section className={cx('min-h-0 flex-col gap-3 p-3 lg:p-4 short:gap-2 lg:short:p-3', view === 'items' ? 'flex' : 'hidden', 'md:flex')} aria-label={t('till.scanSection')}>
          {/* The camera sits beside the scan bar, not on a row of its own: on a laptop that row
              was a whole line of products. */}
          <div className="flex shrink-0 items-stretch gap-2">
            <div className="min-w-0 flex-1">
              <BarcodeInput
                onScan={onScan}
                onText={onScanText}
                onScanError={(info) => flash('error', info.message)}
                scannerHandle={scannerHandle}
                disabled={!shift}
              />
            </div>
            {config?.cameraScanEnabled ? (
              <button
                type="button"
                className="btn shrink-0 flex-col gap-0.5 rounded-2xl px-3 text-xs short:flex-row short:gap-1.5"
                onClick={() => setCameraOpen(true)}
                disabled={!shift}
                aria-label={t('till.scanCamera')}
                title={t('till.scanCamera')}
              >
                <Icon name="camera" size={20} />
                <span className="max-lg:sr-only">{t('till.camera')}</span>
              </button>
            ) : null}
          </div>
          {shiftKnown && !shift ? (
            <div className="banner banner-warn items-center justify-between" data-testid="till-no-shift">
              <span>{t('till.noShiftBanner')}</span>
              <Button size="sm" variant="primary" onClick={() => setDialog('openShift')}>
                {t('till.openShift')}
              </Button>
            </div>
          ) : null}
          <FunctionBar
            placed={placed}
            keyFor={keyOfButton}
            onBlocked={(entry) => flash('warn', `${entry.label}: ${entry.action.disabledReason}`)}
          />
          <JustAdded disabled={!shift} findKey={keyOf('productSearch', 'F2')} />
          <div className="min-h-0 flex-1">
            <ProductGrid onPick={(product) => addProduct(product)} onFind={() => openFind()} findKey={keyOf('productSearch', 'F2')} disabled={!shift} />
          </div>
        </section>

        <section
          className={cx('min-h-0 flex-col overflow-hidden border-slate-200 bg-white md:border-l', view === 'bill' ? 'flex' : 'hidden', 'md:flex')}
          aria-label={t('cart.title')}
        >
          <CartPanel
            interState={interState}
            roundOffMode={roundOffMode}
            disabled={!shift}
            onPay={startPayment}
            canPay={canBill && shift !== null}
            onCustomer={customerEnabled ? () => setDialog('customer') : undefined}
            onDetachCustomer={() => cartActions().attachCustomer(null)}
            onDiscount={() => setDialog('discount')}
            onHold={() => setDialog('hold')}
            onNewSale={() => void startNewSale()}
            onAddItems={() => setView('items')}
            keys={{
              customer: keyOf('customer', 'F3'),
              discount: keyOf('discount', 'F7'),
              hold: keyOf('holdBill', 'F6'),
              newSale: keyOf('newSale', 'F12'),
              payment: keyOf('payment', 'F10'),
            }}
            hints={<ShortcutHints />}
          />
        </section>
      </div>

      {sold && view === 'items' ? (
        <div
          className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-white px-3 py-2 md:hidden"
          style={{ boxShadow: '0 -6px 16px rgb(15 23 42 / 0.08)' }}
        >
          <div>
            <p className="text-xs text-slate-500">
              {t('cart.lines', { count: lines.length })} · {t('till.mobileQty', { qty: quantity(totals.totalQtyMilli) })}
            </p>
            <p className="tabular text-2xl leading-none font-extrabold text-slate-900">₹{amount(totals.grandTotalPaise)}</p>
          </div>
          <Button variant="success" size="lg" disabled={!canBill || !shift} onClick={startPayment}>
            {t('cart.pay')}
          </Button>
        </div>
      ) : null}

      {paymentOpen ? (
        <PaymentScreen
          interState={interState}
          roundOffMode={roundOffMode}
          submitting={submitting || Boolean(pendingSale)}
          onCustomer={pendingSale || !config?.features?.includes('customerContinuity') ? undefined : () => setDialog('customer')}
          error={paymentError}
          upi={config?.upiVpa ? { vpa: config.upiVpa, payeeName: store?.name ?? t('till.store') } : null}
          onUpiChange={setUpiOnDisplay}
          onConfirm={(payments) => void completeSale(payments)}
          onCancel={() => {
            setPaymentOpen(false);
            setPaymentError(null);
            setUpiOnDisplay(null);
          }}
        />
      ) : null}

      {receipt ? (
        <ReceiptDialog
          result={receipt}
          mode={receiptMeta?.mode ?? 'sale'}
          customer={receiptMeta?.customer ?? null}
          savedPaise={receiptMeta?.savedPaise ?? 0}
          onClose={() => {
            setReceipt(null);
            setReceiptMeta(null);
          }}
        />
      ) : null}

      {finding ? (
        <QuickFind
          initialQuery={finding.query}
          actions={actions}
          onClose={() => setFinding(null)}
          onProduct={(product, qtyMilli, amountPaise) => {
            setFinding(null);
            addProduct(product, { qtyMilli, amountPaise });
          }}
          onCustomerPhone={(phone) => {
            setFinding(null);
            setCustomerPhoneHint(phone);
            setDialog('customer');
          }}
          onBarcode={(code) => {
            setFinding(null);
            // Ordered, generation-guarded ingestion instead of a second direct lookup.
            scannerHandle.current?.ingest({ raw: code, source: 'manual' });
          }}
        />
      ) : null}

      {cameraOpen ? (
        <CameraScanner
          paused={paymentOpen}
          onBarcode={(code) => scannerHandle.current?.ingest({ raw: code, source: 'camera' })}
          onClose={() => setCameraOpen(false)}
        />
      ) : null}

      {weighing ? (
        <WeighPad
          product={weighing.product}
          initial={weighing.initial}
          onClose={() => setWeighing(null)}
          onAdd={(qtyMilli) => {
            const product = weighing.product;
            setWeighing(null);
            addProduct(product, { qtyMilli });
          }}
        />
      ) : null}

      {dialog === 'openShift' ? (
        <OpenShiftDialog busy={openingShift} onOpen={(paise) => void openShift(paise)} onClose={() => setDialog(null)} />
      ) : null}

      {dialog === 'closeShift' && shift ? (
        <CloseShiftDialog
          shiftId={shift.id}
          onClose={() => setDialog(null)}
          onClosed={(result) => {
            setDialog(null);
            setShift(null);
            flash(
              result.differencePaise === 0 ? 'good' : 'warn',
              result.differencePaise === 0
                ? t('pos.shiftBalanced')
                : t(result.differencePaise < 0 ? 'pos.shiftShort' : 'pos.shiftOver', { amount: amount(Math.abs(result.differencePaise)) }),
            );
          }}
        />
      ) : null}

      {dialog === 'customer' && !pendingSale ? (
        <CustomerPickerDrawer key={sessionKey + ':' + generation} flowId={sessionKey + ':' + generation}
          current={useCart.getState().customer as Customer | null} initialPhone={customerPhoneHint}
          canCreate={useAuth.getState().can('pos.customer.create')}
          fullDetails={Boolean(config?.customerProfilesEnabled && useAuth.getState().can('customer.profile.edit'))}
          onClose={() => { setDialog(null); setCustomerPhoneHint(undefined); }}
          onRemove={() => { cartActions().attachCustomer(null); setDialog(null); }}
          onSelect={(customer) => {
            if (useCart.getState().generation !== generation) return;
            cartActions().attachCustomer(customer); setDialog(null); setCustomerPhoneHint(undefined); flash('good', t('pos.customerAttached', { name: customer.name }));
          }}
        />
      ) : null}
      {pendingSale ? <Modal title={t('outcomeUnknown')} onClose={() => {}} locked testId="sale-outcome" restoreFocus="none" footer={<Button className="w-full whitespace-normal" busy={submitting} onClick={() => void resolveSubmittedSale()}>{t('resolve')}</Button>}><p>{pendingSale.session === sessionKey ? t('outcomeUnknown') : t('pos.resolveElsewhere')}</p></Modal> : null}

      {dialog === 'discount' ? (
        <BillDiscountDialog
          subtotalPaise={totals.subTotalPaise}
          maxBps={config?.maxBillDiscountBps ?? 0}
          value={billDiscount}
          onApply={(discount) => {
            cartActions().setBillDiscount(discount);
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'openItem' ? (
        <OpenItemDialog
          maxPaise={config?.openItemMaxPaise ?? 0}
          canExceedMax={canOverridePrice}
          onClose={() => setDialog(null)}
          onAdd={(input) => {
            cartActions().addOpenItem({ ...input, pricingInclusive: openItemPriceIncludesTax, noTax: openItemNoTax });
            setDialog(null);
            flash('good', t('pos.openItemAdded', { name: input.name }));
          }}
        />
      ) : null}

      {dialog === 'itemTax' ? (
        <ItemTaxDialog
          lines={lines.filter((line) => line.openItem).map((line) => ({ key: line.key, name: line.productName, taxRateId: line.openItem!.taxRateId }))}
          rates={config?.itemTaxRates ?? []}
          priceIncludesTax={openItemPriceIncludesTax}
          onChoose={(key, rateId) => cartActions().setOpenItemTax(key, (config?.itemTaxRates ?? []).find((rate) => rate.id === rateId) ?? null, openItemNoTax)}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'noTax' ? (
        <NoTaxDialog
          currentPaise={totals.grandTotalPaise}
          reducedPaise={priceCart(lines, billDiscount, interState, roundOffMode, taxRemovalMode).totals.grandTotalPaise}
          mode={taxRemovalMode}
          onApply={(note) => {
            cartActions().setTaxExempt(taxRemovalMode, note);
            setDialog(null);
            flash('good', t('pos.noTaxApplied'));
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'hold' ? (
        <HoldDialog
          bills={heldQuery.isError ? [] : (heldQuery.data ?? null)}
          canHold={sold}
          canResume={!sold}
          onHold={async (label) => {
            const ok = await holdBill(label);
            if (ok) setDialog(null);
          }}
          onResume={async (bill) => {
            const ok = await resumeBill(bill);
            if (ok) setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'help' ? <ShortcutHelp onClose={() => setDialog(null)} /> : null}
    </div>
  );

  async function holdBill(label: string): Promise<boolean> {
    if (pendingSale) return false;
    try {
      const payload: HoldPayload = {
        lines: lines.map((line) => ({
          productId: line.productId,
          qtyMilli: line.qtyMilli,
          ratePaise: line.ratePaise,
          batchId: line.batchId,
          discount: lineDiscountOf(line),
          taxRateId: line.taxRateId,
          gstBps: line.gstBps,
          cessBps: line.cessBps,
          pricingInclusive: line.pricingInclusive,
          hsnCode: line.hsnCode,
          mrpPaise: line.mrpPaise,
          ...(line.openItem ? { openItem: { name: line.openItem.name, taxRateId: line.openItem.taxRateId } } : {}),
        })),
        billDiscount,
        ...(taxExempt ? { taxExempt, taxExemptNote } : {}),
        ...(customerId
          ? {
              customerId,
              customer: { id: customerId, name: customerName ?? '', phone: customerPhone ?? '', stateCode: customerStateCode ?? null },
            }
          : {}),
        ...(note && note.trim() ? { note: note.trim() } : {}),
      };

      // The same register the shift and the holds list use. Sending the principal's value raw
      // would post null on a session that has no terminal, and the API refuses that outright.
      await api.post('/api/pos/holds', {
        terminalId,
        replacesHoldId: sourceHoldId,
        label: label.trim() || tr('pos.holdLabel', { count: lines.length }),
        customerId,
        totalPaise: totals.grandTotalPaise,
        payload,
      });
      cartActions().clear();
      scannerHandle.current?.newGeneration();
      void queryClient.invalidateQueries({ queryKey: ['till', 'holds'] });
      setSourceHoldId(null);
      flash('good', tr('pos.held'));
      return true;
    } catch (error) {
      flash('error', error instanceof ApiError ? errorMessage(language, error.code, error.message) : messageOf(error));
      return false;
    }
  }

  async function resumeBill(bill: HeldSale): Promise<boolean> {
    if (pendingSale) return false;
    if (!config?.features?.includes('customerContinuity')) {
      flash('error', tr('pos.resumeNeedsApi'));
      return false;
    }
    const origin = { ...contextRef.current };
    try {
      // A bill held with No Tax resumes with it only for someone allowed to apply it. Anyone else is
      // told, and decides; the bill is never silently re-taxed or left untaxed.
      const heldTaxExempt = bill.payload?.taxExempt ?? null;
      let keepNoTax = heldTaxExempt !== null;
      if (heldTaxExempt && (!taxRemovalAllowed || !canRemoveTax)) {
        const resumeTaxed = await confirm({
          title: tr('pos.resumeNoTaxTitle'),
          message: taxRemovalAllowed ? tr('pos.resumeNoTaxNotAllowed') : tr('pos.resumeNoTaxStoreOff'),
          confirmLabel: tr('pos.resumeWithTax'),
          cancelLabel: tr('pos.leaveHeld'),
        });
        if (!resumeTaxed) return false;
        keepNoTax = false;
      }

      const { hold } = await api.post<{ hold: HeldSale }>(`/api/pos/holds/${bill.id}/resume`, {});
      const payload = hold.payload;

      // A held bill stores ids and prices. Names, GST and stock come from the catalogue, and
      // the STORED rate wins: a price change since the hold must not alter a bill the
      // customer already agreed to.
      // One lookup for the whole bill where the API offers it; an older API gets one call per line.
      const catalogIds = [...new Set(payload.lines.filter((line) => !line.openItem).map((line) => line.productId))];
      let products: (PosProduct | null)[];
      if (catalogIds.length > 0 && config?.features?.includes('productLookup')) {
        const { products: found } = await api.get<{ products: PosProduct[] }>('/api/catalog/products/lookup', { ids: catalogIds.join(',') });
        const byId = new Map(found.map((product) => [product.id, product]));
        // Same outcome as the per-line path: a product gone from the catalogue refuses the resume, and the bill stays held.
        if (catalogIds.some((id) => !byId.has(id))) throw new ApiError(404, { code: 'PRODUCT_NOT_FOUND', message: 'That product could not be found.' }, undefined);
        products = payload.lines.map((line) => (line.openItem ? null : byId.get(line.productId) ?? null));
      } else {
        products = await Promise.all(
          payload.lines.map((line) =>
            line.openItem
              ? Promise.resolve(null)
              : api
              .get<{ product: PosProduct }>(`/api/catalog/products/${line.productId}`)
              .then((result) => result.product),
          ),
        );
      }

      const attached = hold.customerId ? (await api.get<{ customer: Customer }>('/api/customers/' + hold.customerId + '/summary')).customer : null;
      if (contextRef.current.sessionKey !== origin.sessionKey || cartActions().generation !== origin.generation) return false;
      cartActions().loadLines(
        payload.lines.map((line, index) => {
          const product = products[index] ?? null;
          // Pinned values from the hold win over today's catalogue: a GST, price-mode
          // or MRP change between hold and resume must not silently reprice the bill.
          // Old holds without pins fall back to the catalogue, as before.
          return {
            key: '',
            productId: line.productId,
            productName: product?.name ?? line.productId,
            sku: product?.sku ?? '',
            unit: product?.unit ?? 'PC',
            barcode: product?.barcode ?? null,
            qtyMilli: line.qtyMilli,
            ratePaise: line.ratePaise,
            mrpPaise: line.mrpPaise ?? product?.mrpPaise ?? 0,
            // Kind, value AND the per-unit flag, so the resumed bill prices exactly as it was held.
            ...cartDiscountFields(line.discount),
            taxRateId: line.taxRateId ?? product?.taxRateId ?? 'tax:0',
            gstBps: line.gstBps ?? product?.gstBps ?? 0,
            cessBps: line.cessBps ?? product?.cessBps ?? 0,
            pricingInclusive: line.pricingInclusive ?? product?.pricingInclusive ?? true,
            hsnCode: line.hsnCode ?? product?.hsnCode ?? null,
            allowDecimalQty: product?.allowDecimalQty ?? false,
            stockMilli: product?.stockMilli ?? 0,
            batchId: line.batchId,
            batchNumber: null,
            isExpiryTracked: product?.isExpiryTracked ?? false,
            localName: product?.localName ?? null,
            categoryName: product?.categoryName ?? null,
            expiryDate: product?.nextExpiryDate ?? null,
            ...(line.openItem
              ? {
                  productName: line.openItem.name,
                  sku: OPEN_ITEM_SKU,
                  stockMilli: Number.MAX_SAFE_INTEGER,
                  openItem: { id: crypto.randomUUID(), name: line.openItem.name, taxRateId: line.openItem.taxRateId ?? null },
                }
              : {}),
          };
        }),
      );
      cartActions().setBillDiscount(payload.billDiscount);
      if (keepNoTax && payload.taxExempt) cartActions().setTaxExempt(payload.taxExempt, payload.taxExemptNote ?? null);
      // The customer and note travel with the hold; restore them so the resumed bill
      // keeps its IGST split and its reference. A walk-in hold DETACHES whatever the till
      // still carries, so the previous bill's identity (and GST state code) cannot leak in.
      // The snapshot attaches synchronously (offline-safe); the profile is then rehydrated
      // by id so the resumed bill quotes the current name, phone and state.
      {
        const snapshot = payload.customer ?? null;
        const rehydrate = (id: string) => {
          // Without profile access the API refuses; the hold snapshot is all this till may show.
          if (!useAuth.getState().can('customer.profile.view')) return;
          void api
            .get<{ customer: { id: string; name: string; phone: string; stateCode?: string | null } }>(
              '/api/customers/' + id,
            )
            .then((result) => {
              // The cashier may have picked another customer while the fetch was in
              // flight: refresh the same account only, never overwrite a newer choice.
              if (cartActions().customerId !== id) return;
              cartActions().attachCustomer({
                id: result.customer.id,
                name: result.customer.name,
                phone: result.customer.phone,
                stateCode: result.customer.stateCode ?? null,
              });
            })
            .catch(() => {
              // Offline or refused: the hold snapshot stays authoritative.
            });
        };
        if (snapshot?.id) {
          cartActions().attachCustomer({
            id: snapshot.id,
            name: snapshot.name,
            phone: snapshot.phone,
            stateCode: snapshot.stateCode ?? null,
          });
          rehydrate(snapshot.id);
        } else {
          const resumeCustomerId = payload.customerId ?? hold.customerId ?? null;
          if (resumeCustomerId) {
            // Older holds stored only the id: attach it first (replacing any stale
            // identity synchronously), then upgrade to the live profile.
            cartActions().attachCustomer({
              id: resumeCustomerId,
              name: resumeCustomerId,
              phone: '',
              stateCode: null,
            });
            rehydrate(resumeCustomerId);
          } else {
            cartActions().attachCustomer(null);
          }
        }
        const resumeNote = payload.note ?? null;
        cartActions().setNote(resumeNote);
      }

      cartActions().attachCustomer(attached);
      cartActions().setNote(payload.note ?? null);
      setSourceHoldId(bill.id);
      scannerHandle.current?.newGeneration();
      void queryClient.invalidateQueries({ queryKey: ['till', 'holds'] });
      flash('good', tr('pos.resumed', { hold: bill.holdNumber }));
      return true;
    } catch (error) {
      flash('error', messageOf(error));
      return false;
    }
  }
}

interface HeaderProps {
  shift: Shift | null;
  shiftKnown: boolean;
  shiftFailed: boolean;
  terminal: string | null;
  cashier: string | null;
  storeName: string | null;
  online: boolean;
  heldCount: number;
  summary: ShiftSummary | null;
  canClose: boolean;
  resumeKey: string;
  onOpenShift: () => void;
  onCloseShift: () => void;
  onHold: () => void;
  onHelp: () => void;
  onDisplay: () => void;
}

function LanguageSwitch() {
  const language = useLanguage((state) => state.language);
  const setLanguage = useLanguage((state) => state.setLanguage);
  const t = useT();
  return (
    <div className="seg" role="group" aria-label={t('till.language')} data-testid="till-language">
      {LANGUAGES.map((entry) => (
        <button
          key={entry.code}
          type="button"
          lang={entry.code}
          className={'seg-btn' + (language === entry.code ? ' is-active' : '')}
          aria-pressed={language === entry.code}
          aria-label={entry.label}
          data-testid={`till-language-${entry.code}`}
          onClick={() => setLanguage(entry.code)}
        >
          {'font' in entry ? <span style={{ fontFamily: entry.font }}>{entry.short}</span> : entry.short}
        </button>
      ))}
    </div>
  );
}

function TillHeader({
  shift,
  shiftKnown,
  shiftFailed,
  terminal,
  cashier,
  storeName,
  online,
  heldCount,
  summary,
  canClose,
  resumeKey,
  onOpenShift,
  onCloseShift,
  onHold,
  onHelp,
  onDisplay,
}: HeaderProps) {
  const t = useT();
  const counter = terminal ? t('till.counter', { name: terminal.replace(/^terminal:/, '') }) : t('nav.till');
  const status = shift
    ? { text: t('till.shiftOpen'), className: 'badge-success' }
    : shiftFailed
      ? { text: t('till.shiftUnknown'), className: 'badge-warn' }
      : shiftKnown
        ? { text: t('till.noShift'), className: 'badge-warn' }
        : { text: t('till.checkingShift'), className: '' };
  const detail = [shift ? t('till.since', { time: timeOnly(shift.openedAt) }) : null, cashier, storeName].filter(Boolean).join(' · ');
  const average = summary && summary.billCount > 0 ? Math.round(summary.salesPaise / summary.billCount) : 0;

  return (
    <header className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-4 short:py-1.5">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2">
          <span className="text-base font-bold text-slate-900">{counter}</span>
          <span className={cx('badge', status.className)} data-testid="till-shift-status">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
            {status.text}
          </span>
          {online ? null : (
            <span role="status" data-testid="till-connection" className="badge badge-danger">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
              {t('till.offline')}
            </span>
          )}
        </p>
        <p className="truncate text-xs text-slate-500">{detail}</p>
      </div>

      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        {shift && summary ? (
          <div className="hidden items-stretch rounded-xl border border-slate-200 bg-slate-50 xl:flex" data-testid="till-shift-stats" title={t('till.statsTitle')}>
            <HeaderStat value={String(summary.billCount)} label={t('till.statBills', { count: summary.billCount })} />
            <HeaderStat value={rupeesCompact(summary.salesPaise)} label={t('till.statSales')} />
            <HeaderStat value={summary.billCount > 0 ? rupeesCompact(average) : '—'} label={t('till.statAvg')} />
          </div>
        ) : null}
        <button type="button" className="btn btn-touch px-3" onClick={onDisplay} title={t('till.displayTitle')} aria-label={t('till.displayOpen')}>
          <Icon name="monitor" size={18} />
          <span className="hidden 2xl:inline">{t('till.customerScreen')}</span>
        </button>
        {shift ? (
          <>
            <Button className="whitespace-nowrap max-sm:flex-1" onClick={onHold} data-testid="pos-hold">
              {t('till.heldBills')}
              <Badge tone={heldCount > 0 ? 'primary' : 'neutral'}>{heldCount}</Badge>
              <Kbd className="max-sm:hidden">{resumeKey}</Kbd>
            </Button>
            <Button
              className="whitespace-nowrap max-sm:flex-1"
              data-testid="pos-close-shift"
              onClick={onCloseShift}
              disabled={!canClose}
              title={!canClose ? t('action.why.finishFirst') : undefined}
            >
              {t('till.closeShift')}
            </Button>
          </>
        ) : (
          <Button variant="primary" className="max-sm:flex-1" data-testid="pos-open-shift" onClick={onOpenShift}>
            {t('till.openShift')}
          </Button>
        )}
        {/* A phone has the language in the menu and no keyboard for shortcuts: both would cost a row. */}
        <div className="max-sm:hidden">
          <LanguageSwitch />
        </div>
        <button type="button" className="btn btn-touch max-sm:hidden" aria-label={t('till.help')} title={t('till.helpTitle')} data-testid="pos-help" onClick={onHelp}>
          ?
        </button>
      </div>
    </header>
  );
}

function HeaderStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="border-l border-slate-200 px-3.5 py-1 first:border-l-0">
      <p className="tabular text-[0.95rem] leading-tight font-bold text-slate-900">{value}</p>
      <p className="text-[11px] leading-tight text-slate-500">{label}</p>
    </div>
  );
}

function useShortcutList() {
  const [entries, setEntries] = useState(listShortcuts);
  useEffect(() => {
    let alive = true;
    void loadShortcuts().then(() => {
      if (alive) setEntries(listShortcuts());
    });
    return () => {
      alive = false;
    };
  }, []);
  return entries;
}

/** A shortcut's name in the current language; a name the admin typed is kept as typed. */
function shortcutLabel(t: ReturnType<typeof useT>, entry: { action: string; label?: string | null }): string {
  const key = `shortcut.${entry.action}`;
  return hasKey(key) ? t(key) : (entry.label ?? entry.action);
}

function ShortcutHints() {
  const t = useT();
  const entries = useShortcutList();
  const wanted = ['productSearch', 'customer', 'payment', 'holdBill'];
  const shown = wanted.map((action) => entries.find((entry) => entry.action === action)).filter((entry) => entry !== undefined);
  return (
    <ul className="hidden space-y-1.5 text-sm text-slate-600 lg:block">
      {shown.map((entry) => (
        <li key={entry.action} className="flex items-center justify-between gap-3">
          <span>{entry.action === 'productSearch' ? t('action.nav.find') : shortcutLabel(t, entry)}</span>
          <Kbd>{entry.keyCode}</Kbd>
        </li>
      ))}
    </ul>
  );
}

function ShortcutHelp({ onClose }: { onClose: () => void }) {
  const t = useT();
  const entries = useShortcutList();
  return (
    <Modal title={t('till.help')} subtitle={t('help.subtitle')} onClose={onClose} restoreFocus="barcode" testId="shortcut-help">
      <table className="w-full text-sm">
        <caption className="sr-only">{t('help.caption')}</caption>
        <thead>
          <tr>
            <th scope="col" className="th w-28">
              {t('help.key')}
            </th>
            <th scope="col" className="th">
              {t('help.action')}
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.action} className="border-b border-slate-100">
              <th scope="row" className="cell text-left">
                <Kbd>{entry.keyCode}</Kbd>
              </th>
              <td className="cell">{entry.action === 'productSearch' ? t('help.find') : shortcutLabel(t, entry)}</td>
            </tr>
          ))}
          <tr className="border-b border-slate-100">
            <th scope="row" className="cell text-left">
              <Kbd>Ctrl K</Kbd>
            </th>
            <td className="cell">{t('help.ctrlK', { key: entries.find((entry) => entry.action === 'productSearch')?.keyCode ?? 'F2' })}</td>
          </tr>
          <tr>
            <th scope="row" className="cell text-left">
              <Kbd>Enter</Kbd>
            </th>
            <td className="cell whitespace-normal">{t('help.enter')}</td>
          </tr>
          <tr>
            <th scope="row" className="cell text-left">
              <Kbd>Esc</Kbd>
            </th>
            <td className="cell">{t('help.esc')}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-3 text-xs text-slate-500">
        {t('help.note')}
      </p>
    </Modal>
  );
}

export type { Customer };
